/* ⚠️ GENERATED from app/services/avatarFocus.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   services/avatarFocus.ts — look at the thing being edited.

   ── 🟥 REPORTED ────────────────────────────────────────────────────────────
   "when I want to create an avatar, and I choose something in the face or in
    the head we need a nice zoom in to the part im choosing it"

   🟥 THE CREATOR SHOWS THE WHOLE FIGURE AT ALL TIMES, and a nose on a
      three-hundred-pixel full-body view is about four pixels tall. Every face
      slider was therefore a guess followed by a squint: you moved it, nothing
      you could see changed, and you moved it again.

   🟢 A FOCUS IS TWO NUMBERS AND NOTHING ELSE: how far up the figure to look,
      and how much of the figure's height should fill the view. Both are
      FRACTIONS of the figure, so they hold for a tall body and a short one, and
      for every one of the eleven dimensions that change where the nose is.
      Nothing here is a camera position; the scene turns these into one.

   🟥 AND THE MAP IS FROM THE CONTROL, NOT FROM A TAB. "The face tab" is not a
      place to look — the brow, the jaw and the mouth are a third of a head
      apart, which at this zoom is the difference between filling the view and
      being off the top of it.
   ═══════════════════════════════════════════════════════════════════════════ */

/** The whole figure: what the creator shows when nothing in particular is being edited. */
export const WHOLE = {
  y: 0.5,
  span: 1
};

/* 🟢 Measured up the figure, not the head: on a standing adult the eyes sit at
   about 0.935 of standing height, the chin at 0.87, the shoulders at 0.82.
   These are those proportions, and they are why a jaw slider does not frame
   the forehead. */
const F = {
  // ── the head, tight
  brow: {
    y: 0.945,
    span: 0.17
  },
  eyes: {
    y: 0.940,
    span: 0.16
  },
  nose: {
    y: 0.925,
    span: 0.15
  },
  mouth: {
    y: 0.905,
    span: 0.15
  },
  lips: {
    y: 0.905,
    span: 0.15
  },
  chin: {
    y: 0.890,
    span: 0.17
  },
  jaw: {
    y: 0.900,
    span: 0.20
  },
  cheek: {
    y: 0.920,
    span: 0.18
  },
  ears: {
    y: 0.930,
    span: 0.20
  },
  head: {
    y: 0.930,
    span: 0.24
  },
  face: {
    y: 0.925,
    span: 0.22
  },
  skin: {
    y: 0.925,
    span: 0.24
  },
  hair: {
    y: 0.950,
    span: 0.26
  },
  hat: {
    y: 0.950,
    span: 0.26
  },
  // ── the body
  neck: {
    y: 0.860,
    span: 0.26
  },
  shoulder: {
    y: 0.820,
    span: 0.40
  },
  chest: {
    y: 0.760,
    span: 0.42
  },
  waist: {
    y: 0.620,
    span: 0.42
  },
  hip: {
    y: 0.540,
    span: 0.44
  },
  height: {
    y: 0.500,
    span: 1.00
  },
  build: {
    y: 0.560,
    span: 0.90
  },
  arm: {
    y: 0.700,
    span: 0.62
  },
  leg: {
    y: 0.300,
    span: 0.62
  },
  // ── what they are wearing
  top: {
    y: 0.740,
    span: 0.46
  },
  pants: {
    y: 0.420,
    span: 0.55
  },
  shoes: {
    y: 0.075,
    span: 0.24
  },
  feet: {
    y: 0.075,
    span: 0.24
  }
};

/**
 * Where to look for the control with this key.
 *
 * 🟢 MATCHED ON THE KEY'S OWN WORDS, so a dimension added to avatarFace.ts or
 *    avatarBody.ts is framed correctly the day it appears rather than the day
 *    somebody remembers to add it here. `eyeSpacing`, `f_eyes_hi` and `eyes`
 *    all land on the eyes.
 */
export function focusFor(key, fallback = WHOLE) {
  const k = String(key || '').toLowerCase();
  if (!k) return fallback;
  if (F[k]) return F[k];
  /* longest name first, so `shoulder` is not swallowed by `should`-like prefixes
     and `cheek` is preferred over any shorter accidental hit */
  const names = Object.keys(F).sort((a, b) => b.length - a.length);
  for (const n of names) if (k.includes(n)) return F[n];
  return fallback;
}

/** The focus for a whole tab, used while no single control is being touched. */
export function focusForTab(tab) {
  switch (String(tab || '')) {
    case 'face':
      return F.face;
    case 'hair':
      return F.hair;
    case 'body':
      return {
        y: 0.52,
        span: 0.96
      };
    default:
      return WHOLE;
  }
}
/**
 * A focus → where the camera looks and how far away it sits.
 *
 * 🟥 THE HORIZONTAL FIT STILL MATTERS. A tight focus on a face is limited by
 *    the view's height; a wide one on a heavy body is limited by its width, and
 *    ignoring that crops the shoulders off a broad build at exactly the moment
 *    somebody is adjusting how broad it is.
 *
 * 🟢 Never closer than the near plane would like, and never so far that the
 *    figure shrinks below the whole-body framing — a slider cannot push the
 *    camera somewhere the creator cannot come back from.
 */
export function focusCamera(l, fovDeg, aspect, f) {
  const H = Math.max(1e-3, l.top - l.floor);
  const span = Math.max(0.06, Math.min(1, Number(f.span) || 1));
  const y = Math.max(0, Math.min(1, Number(f.y)));
  const height = H * span * 1.12;
  const width = Math.max(1e-3, l.halfWidth * 2) * 1.3;
  const vFov = fovDeg * Math.PI / 180;
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
  const dV = height / 2 / Math.tan(vFov / 2);
  /* 🟢 THE WIDTH ONLY CONSTRAINS A SHOT WIDE ENOUGH TO CONTAIN THE TORSO. Below
     about four tenths of the figure the frame is a head and a neck: the
     shoulders are not in it, so letting them push the camera back would hold a
     face at arm's length on a broad body and up close on a narrow one — the
     same slider framed differently depending on a build it cannot see. */
  const dH = span >= 0.4 ? width / 2 / Math.tan(hFov / 2) : 0;
  /* 🟥 THE CEILING IS THE SPAN CLAMP ABOVE, NOT A SECOND ONE HERE. `span` is
     already held to at most 1, so `height` is at most the whole figure and
     neither dV nor dH can exceed the whole-figure distance — a slider cannot
     push the camera somewhere the creator cannot come back from, because it
     cannot ask for a span wider than the body. A `Math.min(whole, …)` on top of
     that reads like a safeguard and is dead code: nothing can ever trip it, so
     nothing would ever notice if it were wrong. */
  const distance = Math.max(dV, dH, H * 0.12);
  return {
    targetY: l.floor + H * y,
    distance
  };
}

/** Are these two framings different enough to be worth animating between? */
export function focusChanged(a, b) {
  if (!a || !b) return a !== b;
  return Math.abs(a.y - b.y) > 1e-4 || Math.abs(a.span - b.span) > 1e-4;
}
